import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ordersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, CreditCard, Truck, Shield, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { cartItems, cartTotal, clearCart, sessionId, fetchCart } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [shippingCountry, setShippingCountry] = useState('singapore');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [shippingRates, setShippingRates] = useState({});
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
  });

  // Check for payment success callback
  useEffect(() => {
    const checkoutSessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');
    const cancelled = searchParams.get('cancelled');
    
    if (cancelled) {
      toast.error('Payment was cancelled');
      return;
    }
    
    if (checkoutSessionId) {
      // Poll for payment status
      const pollStatus = async (attempts = 0) => {
        const maxAttempts = 5;
        
        if (attempts >= maxAttempts) {
          toast.error('Payment status check timed out. Please check your email for confirmation.');
          return;
        }
        
        try {
          const response = await ordersApi.getPaymentStatus(checkoutSessionId);
          
          if (response.data.payment_status === 'paid') {
            setPaymentSuccess(true);
            await clearCart();
            toast.success('Payment successful! Thank you for your order.');
            return;
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
            return;
          }
          
          // Continue polling
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } catch (error) {
          console.error('Error checking payment status:', error);
          toast.error('Error checking payment status');
        }
      };
      
      pollStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch shipping rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await ordersApi.getShippingRates();
        setShippingRates(response.data);
      } catch (error) {
        console.error('Error fetching shipping rates:', error);
        // Fallback rates
        setShippingRates({
          singapore: { rate: 12, currency: 'SGD', symbol: '$', payment_provider: 'stripe' },
          dubai: { rate: 15, currency: 'AED', symbol: 'AED ', payment_provider: 'stripe' },
          australia: { rate: 18, currency: 'AUD', symbol: 'A$', payment_provider: 'stripe' },
          uk: { rate: 16, currency: 'GBP', symbol: '£', payment_provider: 'stripe' },
          india: { rate: 99, currency: 'INR', symbol: '₹', payment_provider: 'razorpay' },
        });
      }
    };
    fetchRates();
  }, []);

  const shippingLabels = {
    singapore: { name: 'Singapore', flag: '🇸🇬' },
    dubai: { name: 'Dubai, UAE', flag: '🇦🇪' },
    australia: { name: 'Australia', flag: '🇦🇺' },
    uk: { name: 'United Kingdom', flag: '🇬🇧' },
    india: { name: 'India', flag: '🇮🇳' },
  };

  const currentShipping = shippingRates[shippingCountry] || { rate: 0, currency: 'SGD', symbol: '$' };
  const shipping = currentShipping.rate;
  const total = cartTotal + shipping;
  const paymentProvider = currentShipping.payment_provider;

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const shippingAddress = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        street_address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: shippingCountry,
      };

      const originUrl = window.location.origin;
      const response = await ordersApi.create(sessionId, shippingAddress, originUrl);
      
      if (response.data.payment_provider === 'stripe') {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.payment_provider === 'razorpay') {
        // Handle Razorpay
        await handleRazorpayPayment(response.data);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleRazorpayPayment = async (orderData) => {
    // Check if Razorpay is in demo mode
    if (orderData.note && orderData.note.includes('DEMO_MODE')) {
      toast.info('Razorpay is in demo mode. In production, payment gateway will open here.', {
        duration: 5000,
      });
      // Simulate successful payment for demo
      setTimeout(async () => {
        try {
          await ordersApi.verifyRazorpay(
            'demo_payment_id',
            orderData.razorpay_order_id,
            'demo_signature'
          );
          setPaymentSuccess(true);
          await clearCart();
          toast.success('Demo payment successful!');
        } catch (error) {
          toast.error('Payment verification failed');
        }
        setIsProcessing(false);
      }, 2000);
      return;
    }

    // Real Razorpay integration
    const options = {
      key: orderData.razorpay_key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: orderData.name,
      description: orderData.description,
      order_id: orderData.razorpay_order_id,
      handler: async (response) => {
        try {
          await ordersApi.verifyRazorpay(
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature
          );
          setPaymentSuccess(true);
          await clearCart();
          toast.success('Payment successful! Thank you for your order.');
        } catch (error) {
          toast.error('Payment verification failed');
        }
        setIsProcessing(false);
      },
      prefill: orderData.prefill,
      theme: {
        color: '#C17817',
      },
    };

    // Load Razorpay script if not already loaded
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      document.body.appendChild(script);
    } else {
      const rzp = new window.Razorpay(options);
      rzp.open();
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-[#FDF6E3] pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#5C3D2E] mb-4">
            Order Confirmed!
          </h1>
          <p className="text-[#5C3D2E]/70 mb-8">
            Thank you for your purchase. You will receive a confirmation email shortly.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3]"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0 && !searchParams.get('session_id')) {
    return (
      <div className="min-h-screen bg-[#FDF6E3] pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-[#5C3D2E] mb-4">
            Your cart is empty
          </h1>
          <p className="text-[#5C3D2E]/60 mb-8">
            Add some products before checking out.
          </p>
          <Button
            onClick={() => navigate('/products')}
            className="bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3]"
          >
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF6E3] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C3D2E]/70 hover:text-[#C17817] mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to shopping
        </button>

        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#5C3D2E] mb-10">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-8">
            <form onSubmit={handlePlaceOrder}>
              {/* Contact Information */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-[#D4A853]/10 mb-6">
                <h2 className="font-serif text-xl font-semibold text-[#5C3D2E] mb-6">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#5C3D2E]">First Name</Label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#5C3D2E]">Last Name</Label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[#5C3D2E]">Email Address</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[#5C3D2E]">Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-[#D4A853]/10 mb-6">
                <h2 className="font-serif text-xl font-semibold text-[#5C3D2E] mb-6">
                  Shipping Address
                </h2>

                <div className="mb-6">
                  <Label className="text-[#5C3D2E] mb-3 block">Select Your Country</Label>
                  <RadioGroup
                    value={shippingCountry}
                    onValueChange={setShippingCountry}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                  >
                    {Object.entries(shippingLabels).map(([value, { name, flag }]) => (
                      <div
                        key={value}
                        className={`flex items-center space-x-2 p-3 rounded-xl border cursor-pointer transition-all ${
                          shippingCountry === value
                            ? 'border-[#C17817] bg-[#C17817]/5'
                            : 'border-[#D4A853]/30 hover:border-[#C17817]/50'
                        }`}
                      >
                        <RadioGroupItem value={value} id={value} />
                        <Label htmlFor={value} className="cursor-pointer text-[#5C3D2E] flex items-center gap-2">
                          <span className="text-lg">{flag}</span>
                          <span>{name}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[#5C3D2E]">Street Address</Label>
                    <Input
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#5C3D2E]">City</Label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#5C3D2E]">State/Region</Label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#5C3D2E]">Postal Code</Label>
                      <Input
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-[#D4A853]/30 focus:border-[#C17817]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className={`rounded-2xl p-6 border ${
                paymentProvider === 'razorpay' 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-[#C17817]/5 border-[#C17817]/20'
              }`}>
                <div className="flex items-start gap-4">
                  <CreditCard className={`h-6 w-6 flex-shrink-0 mt-0.5 ${
                    paymentProvider === 'razorpay' ? 'text-blue-600' : 'text-[#C17817]'
                  }`} />
                  <div>
                    <h3 className="font-medium text-[#5C3D2E] mb-1">
                      Payment via {paymentProvider === 'razorpay' ? 'Razorpay' : 'Stripe'}
                    </h3>
                    <p className="text-sm text-[#5C3D2E]/60">
                      {paymentProvider === 'razorpay' 
                        ? 'Pay securely with UPI, Cards, Net Banking & more' 
                        : 'Pay securely with Credit/Debit Cards'}
                    </p>
                    {shippingCountry === 'india' && (
                      <p className="text-xs text-blue-600 mt-2">
                        🇮🇳 Indian customers pay via Razorpay in INR
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button (Desktop) */}
              <div className="hidden lg:block mt-8">
                <Button
                  type="submit"
                  disabled={isProcessing || cartItems.length === 0}
                  className="w-full bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3] py-6 text-lg rounded-xl shadow-lg disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Place Order • ${currentShipping.symbol}${total.toFixed(2)}`
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-md border border-[#D4A853]/10 sticky top-28">
              <h2 className="font-serif text-xl font-semibold text-[#5C3D2E] mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.product_id} className="flex gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-[#5C3D2E] leading-tight">
                        {item.name}
                      </h4>
                      <p className="text-xs text-[#5C3D2E]/60">Qty: {item.quantity}</p>
                    </div>
                    <span className="text-sm font-medium text-[#5C3D2E]">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <Separator className="bg-[#D4A853]/20 my-4" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-[#5C3D2E]/70">
                  <span>Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#5C3D2E]/70">
                  <span className="flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    Shipping ({shippingLabels[shippingCountry]?.name})
                  </span>
                  <span>{currentShipping.symbol}{shipping.toFixed(2)}</span>
                </div>
              </div>

              <Separator className="bg-[#D4A853]/20 my-4" />

              <div className="flex justify-between items-center mb-6">
                <span className="font-semibold text-[#5C3D2E]">Total</span>
                <span className="text-xl font-bold text-[#C17817]">
                  {currentShipping.symbol}{total.toFixed(2)}
                </span>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-4 text-xs text-[#5C3D2E]/50 pt-4 border-t border-[#D4A853]/20">
                <div className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  Secure
                </div>
                <div className="flex items-center gap-1">
                  <Truck className="h-4 w-4" />
                  Trackable
                </div>
              </div>
            </div>

            {/* Mobile Submit Button */}
            <div className="lg:hidden mt-6">
              <Button
                onClick={handlePlaceOrder}
                disabled={isProcessing || cartItems.length === 0}
                className="w-full bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3] py-6 text-lg rounded-xl shadow-lg disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Place Order • ${currentShipping.symbol}${total.toFixed(2)}`
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
